import fs from "node:fs/promises";
import path from "node:path";
import { fileURLToPath } from "node:url";
import type { Plugin } from "vite";

/**
 * React plugin configuration that should be applied to enable design mode.
 * Works with both @vitejs/plugin-react and @vitejs/plugin-react-swc.
 *
 * Usage:
 * ```ts
 * import react from '@vitejs/plugin-react'
 * import { firebuzzDesignMode, getReactPluginConfig } from '@firebuzz/design-mode'
 *
 * export default defineConfig({
 *   plugins: [
 *     react(getReactPluginConfig()),
 *     firebuzzDesignMode(),
 *   ],
 * })
 * ```
 */
export function getReactPluginConfig() {
	const isDesignModeEnabled =
		process.env.NODE_ENV === "development" &&
		process.env.VITE_DESIGN_MODE !== "false";

	if (!isDesignModeEnabled) {
		return {};
	}

	return {
		babel: {
			plugins: [["@react-dev-inspector/babel-plugin", {}]],
		},
	};
}

/**
 * Vite plugin that enables design mode features for Firebuzz templates
 * - Injects Tailwind Play CDN for runtime CSS generation
 * - Injects overlay script for element selection
 *
 * NOTE: You must also configure the React plugin with getReactPluginConfig()
 * to enable element source tracking (works with both React 18 and 19).
 */
export function firebuzzDesignMode(): Plugin {
	// Only enable in development, never in production builds
	const isDesignModeEnabled =
		process.env.NODE_ENV === "development" &&
		process.env.VITE_DESIGN_MODE !== "false";

	const projectRoot = process.cwd();

	const overlayOutputPath = path.resolve(
		projectRoot,
		"./node_modules/.vite-plugin-firebuzz-design-mode/overlay.mjs",
	);

	return {
		name: "vite-plugin-firebuzz-design-mode",
		enforce: "pre",

		async buildStart() {
			if (!isDesignModeEnabled) return;

			// Copy overlay file to a location Vite can serve
			try {
				// Find the overlay source file from the package using import.meta.url
				const currentFileUrl = import.meta.url;
				const currentFilePath = fileURLToPath(currentFileUrl);
				const packageRoot = path.resolve(path.dirname(currentFilePath), "..");
				const overlaySource = path.join(packageRoot, "dist", "overlay.mjs");
				const overlaySourceMap = path.join(
					packageRoot,
					"dist",
					"overlay.mjs.map",
				);

				// Ensure output directory exists
				await fs.mkdir(path.dirname(overlayOutputPath), { recursive: true });

				// Copy the overlay file
				await fs.copyFile(overlaySource, overlayOutputPath);

				// Copy the source map file if it exists
				try {
					await fs.copyFile(overlaySourceMap, `${overlayOutputPath}.map`);
				} catch {
					// Source map is optional, ignore if not found
				}
			} catch (error) {
				console.warn(
					"[Firebuzz Design Mode] Could not copy overlay file:",
					error,
				);
			}
		},

		transformIndexHtml(html) {
			if (!isDesignModeEnabled) return html;

			// Inject Tailwind v4 Browser CDN for runtime CSS generation
			const tailwindCDN =
				'<script src="https://cdn.jsdelivr.net/npm/@tailwindcss/browser@4"></script>';

			// Inject the overlay script
			const overlayPath = overlayOutputPath.replace(projectRoot, "");
			const overlayScript = `<script type="module" src="${overlayPath}"></script>`;

			// Inject both in the head (Tailwind CDN) and before </body> (overlay)
			let modifiedHtml = html.replace("</head>", `${tailwindCDN}</head>`);
			modifiedHtml = modifiedHtml.replace("</body>", `${overlayScript}</body>`);

			return modifiedHtml;
		},
	};
}
